import { NgTemplateOutlet } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, Output } from '@angular/core'
import { RouterModule } from '@angular/router'
import { TranslateModule } from '@ngx-translate/core'

/**
 * One tab in a tab-row. A tab with a `route` is a link (the tab is its own page); a tab without one
 * is a view of the current screen, selected through `active` / `activeChange`.
 */
export interface TabRowItem {
  /** Identity for a view tab, and the value `activeChange` emits. For a link tab, any unique key. */
  id: string
  /** Translated in the row. */
  label: string
  route?: string
  queryParams?: Record<string, string>
  /**
   * Match the URL exactly rather than as a prefix. Needed where a tab's route is a prefix of its
   * siblings' — `/account` is the start of `/account/security`.
   */
  exact?: boolean
  /** A count after the label. Hidden when 0, null or undefined. */
  count?: number | null
  /** 'neutral' is a quiet tally (Campaigns' openings); 'alert' is an unread count, in red. */
  countStyle?: 'neutral' | 'alert'
  /** A small Material icon after the label (e.g. open_in_new, for a tab that leaves the dialog). */
  trailingIcon?: string
  /** Overrides the row's underline for this tab while it is active. */
  activeColor?: 'complimentary'
}

/** Which accent the active underline takes: the primary accent (Pipelines, Campaigns, Account) or the Openings secondary. */
export type TabRowTone = 'primary' | 'secondary'

/**
 * How the row meets the content region it sits in. The row is a white band running the full width
 * of that region, so it breaks out of the gutter around it and re-adds the gutter inside.
 * - `page`: under a title band (screen-header); breaks out of the shell's side gutter.
 * - `page-top`: first thing on the page; breaks out of the top gutter too, sitting flush under the
 *   app header.
 * - `modal`: inside a dialog body (e11-p-4, 1rem at every width).
 * - `none`: no breakout.
 * `page` and `page-top` pin under the app header on a phone, or at every width with `pin`.
 */
export type TabRowBleed = 'page' | 'page-top' | 'modal' | 'none'

/**
 * When a `page` or `page-top` row pins under the app header: on a phone only (the default), or at
 * every width — for a screen whose own header pins beneath the row (the opening and template
 * editors).
 */
export type TabRowPin = 'phone' | 'always'

/**
 * The underline tab row — the one every screen with tabs uses, so they cannot drift apart.
 *
 * It only draws the row. Which tab is active, and whether that is kept in the URL, belongs to the
 * screen: link tabs follow the router; view tabs follow `active`. A screen that hides a tab leaves
 * it out of `tabs`.
 */
@Component({
  selector: 'tab-row',
  standalone: true,
  imports: [NgTemplateOutlet, RouterModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tab-row.component.html',
  styleUrls: ['./tab-row.component.scss'],
})
export class TabRowComponent {
  @Input({ required: true }) tabs: TabRowItem[] = []
  /** The selected view tab's id. Ignored by link tabs, which follow the router. */
  @Input() active?: string | null
  @Input() tone: TabRowTone = 'primary'
  @Input() bleed: TabRowBleed = 'page'
  @Input() pin: TabRowPin = 'phone'
  @Input() ariaLabel = ''

  /** Emits a view tab's id on every click, the current tab included — the screen decides what a repeat click does. */
  @Output() activeChange = new EventEmitter<string>()

  @HostBinding('class') get hostClasses(): string {
    return `bleed-${this.bleed} tone-${this.tone} pin-${this.pin}`
  }

  /* Held as fields, not object literals in the template: a fresh literal on every check is a new
     identity for routerLinkActive to react to. */
  readonly subsetMatch = { paths: 'subset', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' } as const
  readonly exactMatch = { paths: 'exact', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' } as const

  /** Link rows are navigation, not a tablist. */
  get isViewRow(): boolean {
    return this.tabs.some(tab => !tab.route)
  }

  select(tab: TabRowItem): void {
    this.activeChange.emit(tab.id)
  }
}
